import {
  BaseQueryFn,
  createApi,
  EndpointBuilder,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { CameraFolder, GetStreamUrl } from '../screens/externalСamera/store/types';
import { appEnv } from '../config/env';
import { getGlobalAbortController } from './abortController';
import { ExternalFolder, MiddlewareUploadFileResponse, ResponseStatuses, Status } from './types';

export const BASE_URL: string = appEnv.api.site;

const withAuthHeaders = (headers: Headers, getState: () => unknown) => {
  const state = getState() as any;
  const user = state?.auth?.user;

  if (user?.id_con) {
    headers.set('id_con', `${user.id_con}`);
  }
  if (user?.token) {
    headers.set('Authorization', `Bearer ${user.token}`);
  }
  return headers;
};

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'same-origin',
  prepareHeaders: (headers, { getState }) => withAuthHeaders(headers, getState),
  fetchFn: async (input, init) => {
    try {
      return await fetch(input, {
        ...init,
        signal: getGlobalAbortController().signal,
      });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // console.log('Fetch aborted');
        // Возможно, вы захотите выполнить дополнительные действия при прерывании запроса
      } else {
        //   console.error('Fetch error:', error);
      }
      throw error;
    }
  },
});

const SESSION_EXPIRED_MESSAGE = 'Неактуальная сессия';

const isSessionExpiredResponse = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const response = payload as { status?: boolean; message?: string };
  return response.status === false && response.message === SESSION_EXPIRED_MESSAGE;
};
//
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await baseQuery(args, api, extraOptions);
  const hasUnauthorizedStatus = !!result.error && result.error.status === 401;
  const hasExpiredSessionPayload = !!result.data && isSessionExpiredResponse(result.data);

  if (hasUnauthorizedStatus || hasExpiredSessionPayload) {
    api.dispatch({ type: 'auth/logout' });
  }

  if (result.error && result.error.data) {
  }

  return result;
};

export const STREAM_URL = appEnv.api.stream;
export const apiPush = createApi({
  reducerPath: 'api_push',
  baseQuery: fetchBaseQuery({
    baseUrl: appEnv.api.push,
    prepareHeaders: (headers, { getState }) => withAuthHeaders(headers, getState),
  }),
  // baseQuery: fetchBaseQuery({ baseUrl: STREAM_URL }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    subscribe: build.mutation<
      { message: string },
      { newSubscription: PushSubscription; id_con: string }
    >({
      query: ({ newSubscription, id_con }) => ({
        url: '/subscribe',
        method: 'POST',
        body: { newSubscription, id_con },
      }),
    }),
    getCameraStream: build.mutation({
      query: () => ({
        url: '/start-stream',
      }),
    }),
  }),
});

export const apiArchive = createApi({
  reducerPath: 'api_archive',
  baseQuery: fetchBaseQuery({ baseUrl: appEnv.api.archive }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    getFolders: build.mutation<CameraFolder[], { ID_Obj: string }>({
      query: ({ ID_Obj }) => ({
        url: '/get-all-folders',
        method: 'POST',
        body: { ID_Obj },
      }),
      transformResponse(data: { folders: CameraFolder[] }) {
        return data.folders;
      },
    }),
    getStreamArchive: build.mutation<void, GetStreamUrl>({
      query: (body) => ({
        url: '/get-stream',
        method: 'POST',
        body,
      }),
    }),
    removeArchiveStream: build.mutation<void, { folderName: string }>({
      query: ({ folderName }) => ({
        url: '/remove-stream',
        method: 'POST',
        body: { folderName },
      }),
    }),
  }),
});

export const apiAI = createApi({
  reducerPath: 'api_ai',
  baseQuery: fetchBaseQuery({ baseUrl: appEnv.api.ai }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({}),
});

export const apiAIAssistant = createApi({
  reducerPath: 'api_ai_assistant',
  baseQuery: fetchBaseQuery({
    baseUrl: appEnv.api.aiAssistant,
    prepareHeaders: (headers) => {
      if (appEnv.aiAssistantToken) {
        headers.set('token', appEnv.aiAssistantToken);
      }
      return headers;
    },
  }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({}),
});

export const apiEvotor = createApi({
  reducerPath: 'api_evotor',
  baseQuery: fetchBaseQuery({ baseUrl: appEnv.api.evotor }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({}),
});

export const apiStreamServer = createApi({
  reducerPath: 'api_stream_server',
  baseQuery: fetchBaseQuery({ baseUrl: appEnv.api.stream }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    getAllFolders: build.mutation<ExternalFolder[], { ID_Obj: string }>({
      query: ({ ID_Obj }) => ({
        url: '/get-all-folders',
        method: 'POST',
        body: { ID_Obj },
      }),
      transformResponse: (response: any) => response.folders,
    }),
    getStream: build.mutation<
      { status: string },
      { folderName: string; ID_Obj: string; date: string }
    >({
      query: (body) => ({
        url: '/get-stream',
        method: 'POST',
        body,
      }),
    }),
    removeStream: build.mutation<void, { folderName: string }>({
      query: (body) => ({
        url: '/remove-stream',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const apiCourier = createApi({
  reducerPath: 'api_courier',
  baseQuery: fetchBaseQuery({
    baseUrl: appEnv.api.courier,
    prepareHeaders: (headers, { getState }) => withAuthHeaders(headers, getState),
  }),
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({}),
});

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnFocus: true,
  tagTypes: ['FinGetLeftovers'],
  endpoints: (build) => ({
    getTaskStatuses: build.mutation<ResponseStatuses, void>({
      query: () => ({
        url: '/helpDesk/p_AppState.php',
        method: 'GET',
      }),
      transformResponse: ({ status, list }): { status: boolean; list: Status[] } => {
        return {
          status,
          list,
        };
      },
    }),
    getObjCams: build.query<any, { FObjects: string }>({
      query: ({ FObjects }) => ({
        url: `/videoCameras/list?FObjects=${FObjects}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    middlewareUploadFiles: build.mutation<
      MiddlewareUploadFileResponse,
      { path: string; body: FormData }
    >({
      query: ({ path, body }) => ({
        url: path,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetFoldersMutation,
  useGetStreamArchiveMutation,
  useRemoveArchiveStreamMutation,
} = apiArchive;
export const { useSubscribeMutation } = apiPush;
export const { useGetAllFoldersMutation, useGetStreamMutation, useRemoveStreamMutation } =
  apiStreamServer;
export const { useGetTaskStatusesMutation, useMiddlewareUploadFilesMutation, useGetObjCamsQuery } =
  api;
